import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { calendarAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { cn, formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';
import {
  ChevronRight,
  Loader2,
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  Users,
  Briefcase,
  DollarSign,
  FileText,
  Calendar,
  TrendingUp,
  ArrowRight,
  Lock,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const STAGE_COLORS = {
  completed: 'bg-green-500',
  current: 'bg-blue-500',
  pending: 'bg-slate-300',
};

const STAGE_ICON_COLORS = {
  completed: 'text-green-600 bg-green-100',
  current: 'text-blue-600 bg-blue-100',
  pending: 'text-slate-400 bg-slate-100',
};

// Individual Case Progress Component
export const CaseProgressFunnel = ({ caseId, onStageChange }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [changingStage, setChangingStage] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (caseId) {
      loadProgress();
    }
  }, [caseId]);

  const loadProgress = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await calendarAPI.getCaseProgress(caseId);
      setProgress(response.data);
    } catch (err) {
      console.error('Failed to load case progress:', err);
      setError(err.response?.data?.detail || 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (newStage) => {
    setChangingStage(true);
    try {
      await calendarAPI.updateCaseStage(caseId, newStage);
      toast.success(`Case moved to ${newStage.replace('_', ' ')} stage`);
      loadProgress();
      onStageChange?.(newStage);
    } catch (err) {
      toast.error('Failed to update stage');
    } finally {
      setChangingStage(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-6 text-center">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-700 text-sm">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!progress) return null;

  const currentStageIndex = progress.stages.findIndex(s => s.status === 'current');
  const completionPercentage = Math.round((currentStageIndex / (progress.stages.length - 1)) * 100);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg text-slate-900">{progress.case_title}</h3>
          <p className="text-sm text-slate-500">Case Progress Funnel</p>
        </div>
        <Badge variant="outline" className="text-sm">
          {completionPercentage}% Complete
        </Badge>
      </div>

      {/* Progress Bar */}
      <Progress value={completionPercentage} className="h-2" />

      {/* Stages */}
      <div className="relative">
        {/* Connecting line */}
        <div className="absolute top-6 left-6 right-6 h-0.5 bg-slate-200 -z-10" />
        
        <div className="flex justify-between">
          {progress.stages.map((stage, index) => {
            const isCompleted = stage.status === 'completed';
            const isCurrent = stage.status === 'current';
            const isPending = stage.status === 'pending';
            
            return (
              <TooltipProvider key={stage.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className={cn(
                        "flex flex-col items-center w-20 transition-all",
                        !isPending && "cursor-pointer hover:scale-105",
                        isPending && "cursor-default opacity-50"
                      )}
                      onClick={() => !isPending && !changingStage && handleStageChange(stage.id)}
                      disabled={changingStage}
                    >
                      <div className={cn(
                        "w-12 h-12 rounded-full flex items-center justify-center transition-all",
                        STAGE_ICON_COLORS[stage.status]
                      )}>
                        {isCompleted ? (
                          <CheckCircle2 className="h-6 w-6" />
                        ) : isCurrent ? (
                          <Circle className="h-6 w-6 fill-current" />
                        ) : (
                          <Circle className="h-6 w-6" />
                        )}
                      </div>
                      <span className={cn(
                        "text-xs mt-2 text-center font-medium",
                        isCurrent ? "text-blue-600" : isCompleted ? "text-green-600" : "text-slate-400"
                      )}>
                        {stage.name}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-medium">{stage.name}</p>
                    <p className="text-xs text-slate-500">{stage.description}</p>
                    {!isPending && <p className="text-xs text-blue-600 mt-1">Click to move case here</p>}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <Card>
          <CardContent className="p-3 text-center">
            <FileText className="h-5 w-5 text-slate-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-slate-900">{progress.metrics.document_count}</p>
            <p className="text-xs text-slate-500">Documents</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <CheckCircle2 className="h-5 w-5 text-slate-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-slate-900">
              {progress.metrics.completed_tasks}/{progress.metrics.total_tasks}
            </p>
            <p className="text-xs text-slate-500">Tasks Done</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <AlertTriangle className="h-5 w-5 text-amber-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-slate-900">{progress.metrics.upcoming_deadlines}</p>
            <p className="text-xs text-slate-500">Deadlines</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <TrendingUp className="h-5 w-5 text-green-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-slate-900">{progress.metrics.task_completion_rate}%</p>
            <p className="text-xs text-slate-500">Completion</p>
          </CardContent>
        </Card>
      </div>

      {/* Next Deadline Alert */}
      {progress.metrics.next_deadline && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 flex items-center gap-3">
            <Calendar className="h-5 w-5 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium text-amber-800 text-sm">
                Next Deadline: {progress.metrics.next_deadline.title}
              </p>
              <p className="text-xs text-amber-700">
                {new Date(progress.metrics.next_deadline.start_time).toLocaleDateString()}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Master Funnel Component (Partner/Owner only)
export const MasterCaseFunnel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [showCasesDialog, setShowCasesDialog] = useState(false);

  useEffect(() => {
    loadMasterFunnel();
  }, []);

  const loadMasterFunnel = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await calendarAPI.getMasterFunnel();
      setFunnel(response.data);
    } catch (err) {
      console.error('Failed to load master funnel:', err);
      if (err.response?.status === 403) {
        setError('Only partners and owners can view the master funnel');
      } else {
        setError(err.response?.data?.detail || 'Failed to load funnel');
      }
    } finally {
      setLoading(false);
    }
  };

  const openStageCases = (stage) => {
    setSelectedStage(stage);
    setShowCasesDialog(true);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto" />
          <p className="text-slate-500 mt-2">Loading master funnel...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-6 text-center">
          <Lock className="h-8 w-8 text-amber-500 mx-auto mb-2" />
          <p className="text-amber-700">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!funnel) return null;

  // Calculate max for funnel visualization
  const maxCases = Math.max(...funnel.funnel.map(s => s.case_count), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Master Case Pipeline</h2>
          <p className="text-sm text-slate-500">
            {funnel.total_cases} total cases • {formatCurrency(funnel.total_value)} total value
          </p>
        </div>
        <Badge className="bg-purple-100 text-purple-700 border-purple-200">
          <Eye className="h-3 w-3 mr-1" />
          Partners Only
        </Badge>
      </div>

      {/* Funnel Visualization */}
      <Card>
        <CardContent className="py-6">
          <div className="space-y-3">
            {funnel.funnel.map((stage, index) => {
              const widthPercent = (stage.case_count / maxCases) * 100;
              const colors = [
                'bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-purple-500',
                'bg-fuchsia-500', 'bg-pink-500', 'bg-rose-500', 'bg-red-500',
                'bg-orange-500', 'bg-green-500'
              ];
              
              return (
                <div key={stage.id} className="relative">
                  <div className="flex items-center gap-4">
                    <div className="w-28 text-right">
                      <span className="text-sm font-medium text-slate-700">{stage.name}</span>
                    </div>
                    <div className="flex-1">
                      <button
                        onClick={() => openStageCases(stage)}
                        className="w-full group"
                      >
                        <div 
                          className={cn(
                            "h-10 rounded-r-full transition-all group-hover:opacity-80",
                            colors[index % colors.length]
                          )}
                          style={{ width: `${Math.max(widthPercent, 5)}%` }}
                        >
                          <div className="h-full flex items-center justify-end pr-3">
                            <span className="text-white text-sm font-bold">
                              {stage.case_count}
                            </span>
                          </div>
                        </div>
                      </button>
                    </div>
                    <div className="w-24 text-right">
                      <span className="text-sm text-slate-500">
                        {formatCurrency(stage.total_value)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {funnel.funnel.slice(0, 5).map((stage) => (
          <Card key={stage.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openStageCases(stage)}>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-900">{stage.case_count}</p>
              <p className="text-xs text-slate-500 mt-1">{stage.name}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Stage Cases Dialog */}
      <Dialog open={showCasesDialog} onOpenChange={setShowCasesDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              {selectedStage?.name} - {selectedStage?.case_count} Cases
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[400px]">
            {selectedStage?.cases?.length > 0 ? (
              <div className="space-y-2">
                {selectedStage.cases.map((caseItem) => (
                  <Card 
                    key={caseItem.case_id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => {
                      navigate(`/cases/${caseItem.case_id}`);
                      setShowCasesDialog(false);
                    }}
                  >
                    <CardContent className="p-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{caseItem.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {caseItem.practice_area}
                          </Badge>
                          {caseItem.value > 0 && (
                            <span className="text-xs text-slate-500">
                              {formatCurrency(caseItem.value)}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Briefcase className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                <p>No cases in this stage</p>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseProgressFunnel;
